import React, { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { useTheme } from "../context/ThemeContext";
import ImportJobStepper from "./ImportJobStepper";
import MaintenanceImportJobDeleteDialog from "./MaintenanceImportJobDeleteDialog";
import {
  deleteMaintenanceImportJob,
  getMaintenanceImportJob,
} from "../services";

const IMPORT_JOB_STEPS = [
  "Upload",
  "Extract",
  "Review & Verify",
  "Submit",
  "Completed",
];
const DUPLICATES_PER_PAGE = 50;
const IMPORTED_RECORDS_PER_PAGE = 50;

export function formatDurationSeconds(value) {
  if (value == null || value === "") return null;
  const total = Number(value);
  if (!Number.isFinite(total)) return null;
  const seconds = Math.max(0, Math.round(total));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

export function formatExistingJobId(jobId) {
  if (jobId == null || jobId === "") return "—";
  const numeric = Number(jobId);
  if (!Number.isFinite(numeric)) return String(jobId);
  return `JOB-${String(Math.trunc(numeric)).padStart(3, "0")}`;
}

export function fileStatusLabel(status) {
  if (status === "imported") return "Done";
  if (status === "extracted") return "Extracted";
  if (status === "failed") return "Failed";
  if (status === "verified") return "Verified";
  if (status === "draft") return "Draft";
  if (status === "uploaded") return "Uploaded";
  return status || "—";
}

export function timelineEventLabel(event) {
  if (event === "extract_started") return "Extraction started";
  if (event === "extract_finished") return "Extraction finished";
  if (event === "submit_started") return "Submission started";
  if (event === "submit_finished") return "Submission finished";
  return event || "";
}

function formatTimestamp(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function categoryLabel(type) {
  const raw = String(type || "").trim().toLowerCase();
  if (raw === "m" || raw === "maintenance") return "Maintenance";
  if (raw === "c" || raw === "construction") return "Construction";
  if (raw === "o" || raw === "other") return "Other";
  return displayValue(type);
}

function flushReasonLabel(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "DE" || raw === "DEAD END" || raw === "DEAD_END") {
    return "Dead End";
  }
  if (raw === "PWQ" || raw === "POOR WATER QUALITY") {
    return "Poor Water Quality";
  }
  if (raw === "N" || raw === "NITRIFICATION") return "Nitrification";
  return displayValue(value);
}

function flushingDeviceLabel(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "HY" || raw === "HYDRANT") return "Hydrant";
  if (raw === "BO" || raw === "BLOWOFF" || raw === "BLOW OFF") return "Blowoff";
  return displayValue(value);
}

const DUPLICATE_DIFF_COLOR = "#f59e0b";

function duplicateFieldDiffers(item, field) {
  return (item?.differingFields || []).includes(field);
}

function duplicateSideCellStyle(colors, side, differs) {
  if (differs) {
    return {
      backgroundColor: "rgba(245, 158, 11, 0.18)",
      color: DUPLICATE_DIFF_COLOR,
    };
  }
  if (side === "uploaded") {
    return { backgroundColor: `${colors.primary}1a` };
  }
  return { backgroundColor: colors.background };
}

function fileResultNote(file, { hideStagingCounts = false } = {}) {
  if (hideStagingCounts) return "—";
  const imported = Number(file?.importedRows) || 0;
  const duplicates = Number(file?.duplicateRows) || 0;
  const failed = Number(file?.failedRows) || 0;
  const parts = [];
  parts.push(`${imported} imported`);
  parts.push(`${duplicates} duplicate${duplicates === 1 ? "" : "s"}`);
  if (failed > 0) {
    parts.push(`${failed} failed`);
  }
  return parts.join(" · ");
}

export default function MaintenanceImportJobDetails({ jobId, onBack }) {
  const { colors } = useTheme();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [duplicatesPage, setDuplicatesPage] = useState(1);
  const [importedPage, setImportedPage] = useState(1);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);

  const handleDeleteConfirm = async () => {
    const target = jobToDelete;
    if (!target || deletingJobId != null) return;
    setDeletingJobId(target.id);
    try {
      const response = await deleteMaintenanceImportJob(target.id);
      const payload = response?.data || response;
      if (payload?.status === false) {
        toast.error(payload?.message || "Unable to delete job");
        return;
      }
      setJobToDelete(null);
      toast.success(`${target.jobName || "Job"} deleted`);
      onBack?.();
    } catch (err) {
      const payload = err?.response?.data?.data || err?.response?.data || {};
      toast.error(payload?.message || err?.message || "Unable to delete job");
    } finally {
      setDeletingJobId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadJob = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getMaintenanceImportJob(jobId, {
          rows_page: 1,
          rows_per_page: 50,
          duplicates_page: duplicatesPage,
          duplicates_per_page: DUPLICATES_PER_PAGE,
          page: importedPage,
          per_page: IMPORTED_RECORDS_PER_PAGE,
        });
        if (cancelled) return;
        const payload = response?.data || response;
        if (payload?.status === false || !payload?.job) {
          setJob(null);
          setError(payload?.message || "Job not found");
          return;
        }
        setJob(payload.job);
      } catch (err) {
        if (cancelled) return;
        const message =
          err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          "Unable to load job details";
        setJob(null);
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadJob();
    return () => {
      cancelled = true;
    };
  }, [jobId, duplicatesPage, importedPage]);

  const duplicatesTotal = Number(job?.duplicatesTotal) || 0;
  const duplicatesPerPage = Number(job?.duplicatesPerPage) || DUPLICATES_PER_PAGE;
  const duplicatesTotalPages =
    duplicatesTotal > 0
      ? Math.ceil(duplicatesTotal / duplicatesPerPage)
      : 0;
  const importedRecords = Array.isArray(job?.importedRecords)
    ? job.importedRecords
    : [];
  const importedRecordsTotal = Number(job?.importedRecordsTotal) || 0;
  const importedRecordsPerPage =
    Number(job?.importedRecordsPerPage) || IMPORTED_RECORDS_PER_PAGE;
  const importedTotalPages =
    importedRecordsTotal > 0
      ? Math.ceil(importedRecordsTotal / importedRecordsPerPage)
      : 0;
  const importedShowingCount = importedRecords.length;
  const durationLabel = formatDurationSeconds(job?.submitDuration);
  const submittedAt = formatTimestamp(job?.submitFinishedAt || job?.submitStartedAt);
  const jobCode = job?.jobName || formatExistingJobId(jobId);
  const showDangerZone = job?.status === "completed";
  const hasStagingRows = Number(job?.rowsTotal) > 0;
  const hideStagingDerivedUi =
    job?.status === "completed" && !hasStagingRows;

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2
              className="text-xl font-bold text-transparent bg-clip-text"
              style={{
                backgroundImage: colors.gradient,
                WebkitTextFillColor: "transparent",
              }}
            >
              {job?.jobName || "Job details"}
            </h2>
            {job?.status === "completed" ? (
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{
                  color: "#22c55e",
                  backgroundColor: "#22c55e22",
                  border: "1px solid #22c55e55",
                }}
              >
                Completed
              </span>
            ) : null}
          </div>
          <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            {job
              ? [
                  submittedAt !== "—" ? `Submitted ${submittedAt}` : null,
                  durationLabel ? `Duration ${durationLabel}` : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Submission details are not available."
              : loading
                ? "Loading job details..."
                : error || "Job details are not available."}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          disabled={deletingJobId != null}
          className="shrink-0 px-5 py-2.5 rounded-lg text-white font-semibold shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundImage: colors.gradient }}
        >
          ← Import Jobs
        </button>
      </div>

      {showDangerZone && job && !loading && !error ? (
        <section
          className="rounded-xl border px-4 py-3"
          style={{
            backgroundColor: "rgba(239, 68, 68, 0.04)",
            borderColor: "rgba(239, 68, 68, 0.35)",
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p
              className="min-w-0 text-[13px] leading-snug"
              style={{ color: colors.textSecondary }}
            >
              Permanently delete {jobCode} and its uploaded files.
            </p>
            <button
              type="button"
              aria-label={`Delete ${jobCode}`}
              disabled={deletingJobId != null}
              onClick={() => setJobToDelete(job)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ef4444]"
              style={{
                backgroundColor: "transparent",
                borderColor: "#ef4444",
                color: "#ef4444",
              }}
              onMouseEnter={(event) => {
                if (event.currentTarget.disabled) return;
                event.currentTarget.style.backgroundColor =
                  "rgba(239, 68, 68, 0.1)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <Trash2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              Delete {jobCode}
            </button>
          </div>
        </section>
      ) : null}

      {loading ? (
        <div
          className="rounded-xl border px-6 py-12 text-center text-sm"
          style={{ backgroundColor: colors.inputBg, borderColor: colors.border, color: colors.textSecondary }}
        >
          Loading job details...
        </div>
      ) : error ? (
        <div
          className="rounded-xl border px-6 py-12 text-center text-sm"
          style={{ backgroundColor: colors.inputBg, borderColor: colors.border, color: colors.textSecondary }}
        >
          {error}
        </div>
      ) : !job ? (
        <div
          className="rounded-xl border px-6 py-12 text-center text-sm"
          style={{ backgroundColor: colors.inputBg, borderColor: colors.border, color: colors.textSecondary }}
        >
          Job details are not available.
        </div>
      ) : (
        <>
          <div
            className="rounded-xl border px-4 py-5"
            style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
          >
            <ImportJobStepper
              steps={IMPORT_JOB_STEPS}
              currentIndex={job.status === "completed" ? IMPORT_JOB_STEPS.length - 1 : 3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: "Rows found",
                value: job.extractedRows,
                title: "Rows read from the uploaded files",
              },
              {
                label: "New records added",
                value: job.importedRows,
                title: "Rows saved as new maintenance records",
              },
              {
                label: "Skipped (already exist)",
                value: job.duplicateRows,
                title: "Rows skipped because an identical record already exists",
              },
              {
                label: "Failed rows",
                value: job.failedRows,
                title: "Rows that could not be saved",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-xl border px-4 py-4"
                style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
                title={card.title}
              >
                <p className="text-xs uppercase tracking-wide" style={{ color: colors.textSecondary }}>
                  {card.label}
                </p>
                <p className="mt-1 text-2xl font-semibold" style={{ color: colors.text }}>
                  {Number(card.value) || 0}
                </p>
              </div>
            ))}
          </div>

          <section
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
          >
            <div className="px-4 py-3 border-b" style={{ borderColor: colors.border }}>
              <h3
                className="font-semibold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                }}
              >
                Files & their records
              </h3>
            </div>
            {(job.files || []).length === 0 ? (
              <p className="px-4 py-8 text-sm text-center" style={{ color: colors.textSecondary }}>
                No files are recorded for this job.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr style={{ color: colors.textSecondary }}>
                      <th className="px-4 py-3 font-medium">File</th>
                      <th className="px-4 py-3 font-medium">Rows</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(job.files || []).map((file) => (
                      <tr key={file.fileId} className="border-t" style={{ borderColor: colors.border, color: colors.text }}>
                        <td className="px-4 py-3">{displayValue(file.fileName)}</td>
                        <td className="px-4 py-3">{Number(file.extractedRows) || 0}</td>
                        <td className="px-4 py-3">
                          <span
                            className="rounded-full px-2 py-1 text-xs font-semibold"
                            style={{
                              color: file.status === "imported" ? "#22c55e" : colors.text,
                              backgroundColor: file.status === "imported" ? "#22c55e22" : colors.background,
                            }}
                          >
                            {fileStatusLabel(file.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ color: colors.textSecondary }}>
                          {fileResultNote(file, {
                            hideStagingCounts: hideStagingDerivedUi,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {job.status === "completed" ? (
            <section
              className="rounded-xl border overflow-hidden"
              style={{
                backgroundColor: colors.inputBg,
                borderColor: colors.border,
              }}
            >
              <div
                className="border-b px-4 py-3"
                style={{ borderColor: colors.border }}
              >
                <h3
                  className="font-semibold text-transparent bg-clip-text"
                  style={{
                    backgroundImage: colors.gradient,
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Imported records
                </h3>
                <p
                  className="mt-1 text-xs"
                  style={{ color: colors.textSecondary }}
                >
                  {importedRecordsTotal === 0
                    ? "0 records imported"
                    : `Showing ${importedShowingCount} of ${importedRecordsTotal}`}
                </p>
              </div>
              {importedRecordsTotal === 0 ? (
                <p
                  className="px-4 py-8 text-center text-sm"
                  style={{ color: colors.textSecondary }}
                >
                  No records were imported by this job.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-[1100px] w-full text-left text-sm">
                      <thead>
                        <tr style={{ color: colors.textSecondary }}>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Date
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Work Order
                          </th>
                          <th className="min-w-[180px] px-4 py-3 font-medium">
                            Location
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Gallons
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            GPM
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Minutes
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Category
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Post-Flush Residual
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            DE/PWQ
                          </th>
                          <th className="whitespace-nowrap px-4 py-3 font-medium">
                            Flushing Device
                          </th>
                          <th className="min-w-[160px] px-4 py-3 font-medium">
                            File
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedRecords.map((row) => (
                          <tr
                            key={`${row.recordId}-${row.jobFileId}`}
                            className="border-t"
                            style={{
                              borderColor: colors.border,
                              color: colors.text,
                            }}
                          >
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.date)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.work_order)}
                            </td>
                            <td
                              className="max-w-[240px] px-4 py-3"
                              title={row.location || undefined}
                              style={{
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {displayValue(row.location)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.flow_value)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.flow_rate_value)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.flush_duration)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {categoryLabel(row.type)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {displayValue(row.post_flush_residual)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {flushReasonLabel(row.flush_reason)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3">
                              {flushingDeviceLabel(row.flushing_device)}
                            </td>
                            <td
                              className="max-w-[200px] px-4 py-3"
                              title={row.fileName || undefined}
                              style={{
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {displayValue(row.fileName)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {importedTotalPages > 1 ? (
                    <div
                      className="flex items-center justify-end gap-3 border-t px-4 py-3"
                      style={{ borderColor: colors.border }}
                    >
                      <button
                        type="button"
                        disabled={importedPage <= 1}
                        onClick={() =>
                          setImportedPage((current) => Math.max(1, current - 1))
                        }
                        className="rounded-lg border px-4 py-2 text-sm"
                        style={{
                          backgroundColor: colors.background,
                          borderColor: colors.border,
                          color: colors.text,
                          opacity: importedPage <= 1 ? 0.5 : 1,
                        }}
                      >
                        Previous
                      </button>
                      <span
                        className="text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        Page {importedPage} of {importedTotalPages}
                      </span>
                      <button
                        type="button"
                        disabled={importedPage >= importedTotalPages}
                        onClick={() =>
                          setImportedPage((current) => current + 1)
                        }
                        className="rounded-lg border px-4 py-2 text-sm"
                        style={{
                          backgroundColor: colors.background,
                          borderColor: colors.border,
                          color: colors.text,
                          opacity:
                            importedPage >= importedTotalPages ? 0.5 : 1,
                        }}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          ) : null}

          {!hideStagingDerivedUi ? (
          <section
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
          >
            <div className="px-4 py-3 border-b" style={{ borderColor: colors.border }}>
              <h3
                className="font-semibold text-transparent bg-clip-text"
                style={{
                  backgroundImage: colors.gradient,
                  WebkitTextFillColor: "transparent",
                }}
              >
                Duplicate records (uploaded vs existing in DB)
              </h3>
              <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                Left side is the uploaded row that was skipped. Right side is the
                record already in the database. Amber marks a value that differs.
              </p>
            </div>
            {(job.duplicates || []).length === 0 ? (
              <p className="px-4 py-8 text-sm text-center" style={{ color: colors.textSecondary }}>
                No duplicate records for this job.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr style={{ color: colors.textSecondary }}>
                      <th className="px-4 py-3 font-medium">Row</th>
                      <th className="px-4 py-3 font-medium">File</th>
                      <th className="px-4 py-3 font-medium">Work Order</th>
                      <th className="px-4 py-3 font-medium">Uploaded date</th>
                      <th className="px-4 py-3 font-medium">Uploaded location</th>
                      <th className="px-4 py-3 font-medium">Uploaded gallons</th>
                      <th className="px-4 py-3 font-medium">Existing DB ID</th>
                      <th className="px-4 py-3 font-medium">Existing date</th>
                      <th className="px-4 py-3 font-medium">Existing location</th>
                      <th className="px-4 py-3 font-medium">Existing gallons</th>
                      <th className="px-4 py-3 font-medium">Existing job</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(job.duplicates || []).map((item) => {
                      const uploaded = item.uploaded || {};
                      const existing = item.existing || {};
                      return (
                        <tr key={item.rowId} className="border-t" style={{ borderColor: colors.border, color: colors.text }}>
                          <td className="px-4 py-3">{displayValue(item.sourceRowNumber)}</td>
                          <td className="px-4 py-3">{displayValue(item.fileName)}</td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "uploaded",
                              duplicateFieldDiffers(item, "work_order"),
                            )}
                          >
                            {displayValue(uploaded.work_order)}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "uploaded",
                              duplicateFieldDiffers(item, "date"),
                            )}
                          >
                            {displayValue(uploaded.date)}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "uploaded",
                              duplicateFieldDiffers(item, "location"),
                            )}
                          >
                            {displayValue(uploaded.location)}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "uploaded",
                              duplicateFieldDiffers(item, "flow_value"),
                            )}
                          >
                            {displayValue(uploaded.flow_value)}
                          </td>
                          <td className="px-4 py-3">
                            {item.existingRecordId != null ? `#${item.existingRecordId}` : "—"}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "existing",
                              duplicateFieldDiffers(item, "date"),
                            )}
                          >
                            {item.existing ? displayValue(existing.date) : "—"}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "existing",
                              duplicateFieldDiffers(item, "location"),
                            )}
                          >
                            {item.existing ? displayValue(existing.location) : "—"}
                          </td>
                          <td
                            className="px-4 py-3"
                            style={duplicateSideCellStyle(
                              colors,
                              "existing",
                              duplicateFieldDiffers(item, "flow_value"),
                            )}
                          >
                            {item.existing ? displayValue(existing.flow_value) : "—"}
                          </td>
                          <td className="px-4 py-3">{formatExistingJobId(item.existingJobId)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {duplicatesTotalPages > 1 ? (
              <div className="flex items-center justify-end gap-3 px-4 py-3 border-t" style={{ borderColor: colors.border }}>
                <button
                  type="button"
                  disabled={duplicatesPage <= 1}
                  onClick={() => setDuplicatesPage((current) => Math.max(1, current - 1))}
                  className="px-4 py-2 rounded-lg border text-sm"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                    opacity: duplicatesPage <= 1 ? 0.5 : 1,
                  }}
                >
                  Previous
                </button>
                <span className="text-sm" style={{ color: colors.textSecondary }}>
                  Page {duplicatesPage} of {duplicatesTotalPages}
                </span>
                <button
                  type="button"
                  disabled={duplicatesPage >= duplicatesTotalPages}
                  onClick={() => setDuplicatesPage((current) => current + 1)}
                  className="px-4 py-2 rounded-lg border text-sm"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                    opacity: duplicatesPage >= duplicatesTotalPages ? 0.5 : 1,
                  }}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>
          ) : null}

          <section
            className="rounded-xl border px-4 py-4"
            style={{ backgroundColor: colors.inputBg, borderColor: colors.border }}
          >
            <h3
              className="font-semibold text-transparent bg-clip-text"
              style={{
                backgroundImage: colors.gradient,
                WebkitTextFillColor: "transparent",
              }}
            >
              Job timeline
            </h3>
            {(job.timeline || []).length === 0 ? (
              <p className="mt-3 text-sm" style={{ color: colors.textSecondary }}>
                No timeline timestamps are available for this job.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {(job.timeline || []).map((event) => {
                  const duration = formatDurationSeconds(event.durationSeconds);
                  return (
                    <li key={`${event.event}-${event.at}`} className="text-sm" style={{ color: colors.textSecondary }}>
                      {formatTimestamp(event.at)} · {timelineEventLabel(event.event)}
                      {duration ? ` (${duration})` : ""}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      <MaintenanceImportJobDeleteDialog
        open={jobToDelete != null}
        job={jobToDelete}
        busy={deletingJobId != null}
        colors={colors}
        title={`Delete ${jobCode}?`}
        body={`This will permanently delete ${jobCode}. Its imported maintenance records will be archived and removed, and its uploaded files and extracted rows will be deleted.`}
        confirmPhrase={jobCode}
        onCancel={() => {
          if (deletingJobId != null) return;
          setJobToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
